import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import {
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Card,
  IconButton,
  Tooltip,
} from "@mui/material";
import CustomButton from "../../../components/button";
import { MinusSquare, Plus, Calendar, Video, ChevronDown } from "react-feather";
import moment from "moment";
import AlertModal from "../../../components/modal";
import AddVideoDialog from "../../../components/modal/videodialog";
import { v4 as uuidv4 } from "uuid";

const validationSchema = yup.object().shape({
  name: yup.string().required("Campaign name is required"),
  client: yup.string().required("Client name is required"),
});

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
  container: {
    display: "flex",
    gap: "40px",
    width: "100%",
    height: "100%",
  },
  leftSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  rightSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
};

export default function EditCampaignStep({
  onChange = () => {},
  goNext = () => {},
  formikRef,
  data,
  videos,
  initialCalendars = [],
  handleStatus,
  setToastStatus,
  setToastDescription,
}) {
  const [schedules, setSchedules] = useState([]);
  const [originalSchedules, setOriginalSchedules] = useState([]); // Store original values from backend
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentScheduleIndex, setCurrentScheduleIndex] = useState(null);
  const [alertDeleteSchedule, setAlertDeleteSchedule] = useState(false);
  const [scheduleToRemove, setScheduleToRemove] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const handleAddSchedule = () => {
    const newSchedule = {
      id: uuidv4(),
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      videos: [],
    };
    const newSchedules = [...schedules, newSchedule];
    setSchedules(newSchedules);

    // Open the newly added accordion (last one)
    setExpandedAccordion(newSchedules.length - 1);

    // Notify parent about schedule changes
    onChange({
      schedules: newSchedules,
    });
  };

  const handleAccordionChange = (index) => (event, isExpanded) => {
    // Only one accordion can be expanded at a time
    setExpandedAccordion(isExpanded ? index : null);
  };

  const handleDeleteSchedule = (index) => {
    setScheduleToRemove(index);
    setAlertDeleteSchedule(true);
  };

  const executeDeleteSchedule = () => {
    if (scheduleToRemove !== null) {
      const newSchedules = schedules.filter((_, i) => i !== scheduleToRemove);
      setSchedules(newSchedules);

      // If we're deleting the expanded accordion, close it
      if (expandedAccordion === scheduleToRemove) {
        setExpandedAccordion(null);
      } else if (expandedAccordion > scheduleToRemove) {
        // If we're deleting an accordion before the expanded one, adjust the index
        setExpandedAccordion(expandedAccordion - 1);
      }

      // Notify parent about schedule changes
      onChange({
        schedules: newSchedules,
      });
    }
    setAlertDeleteSchedule(false);
    setScheduleToRemove(null);
  };

  const validateDateInput = (value, field, schedule) => {
    if (!value || !value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null; // Don't validate incomplete dates
    }

    const selectedDate = moment(value);
    const today = moment().startOf("day");

    // Check if the date is valid
    if (!selectedDate.isValid()) {
      return `Data introdusă nu este validă.`;
    }

    // Find the original schedule to compare with
    const originalSchedule = originalSchedules.find(
      (orig) => orig.id === schedule.originalId
    );

    // For existing campaigns, only validate past dates if the user actually modified the date
    if (originalSchedule && schedule.originalId) {
      const originalValue = originalSchedule[field];
      const hasNotModifiedDate = originalValue === value;

      // If the date hasn't been modified from the original, skip the past date validation
      if (hasNotModifiedDate) {
        // Still validate year range and date relationships, but skip past date check
      } else {
        // Only apply past date validation if the user actually modified the date
        if (selectedDate.isBefore(today)) {
          return `Data ${
            field === "startDate" ? "de inceput" : "de sfarsit"
          } nu poate fi in trecut.`;
        }
      }
    } else {
      // For new schedules, always validate past dates
      if (selectedDate.isBefore(today)) {
        return `Data ${
          field === "startDate" ? "de inceput" : "de sfarsit"
        } nu poate fi in trecut.`;
      }
    }

    // Don't allow invalid years (beyond reasonable range)
    const year = selectedDate.year();
    if (year < 2024 || year > 2030) {
      return `Anul selectat (${year}) nu este valid.`;
    }

    // Check relationship between start and end dates
    if (field === "endDate" && schedule.startDate) {
      const startDate = moment(schedule.startDate);
      if (startDate.isValid() && selectedDate.isBefore(startDate)) {
        return "Data de sfarsit nu poate fi inainte de data de inceput.";
      }
    }

    if (field === "startDate" && schedule.endDate) {
      const endDate = moment(schedule.endDate);
      if (endDate.isValid() && selectedDate.isAfter(endDate)) {
        return "Data de inceput nu poate fi dupa data de sfarsit.";
      }
    }

    return null; // No validation errors
  };

  const handleScheduleChange = (index, field, value) => {
    // Always update the schedule first to allow typing
    const newSchedules = schedules.map((schedule, i) =>
      i === index ? { ...schedule, [field]: value } : schedule
    );
    setSchedules(newSchedules);

    // Notify parent about schedule changes
    onChange({
      schedules: newSchedules,
    });
  };

  const handleScheduleBlur = (index, field, value) => {
    const schedule = schedules[index];
    const validationError = validateDateInput(value, field, schedule);

    if (validationError) {
      handleStatus(validationError, setToastStatus, setToastDescription);
      // Optionally, you could reset the field to previous valid value here
      // But for better UX, let's just show the error and let user fix it
    }
  };

  const handleOpenVideoModal = (scheduleIndex) => {
    setCurrentScheduleIndex(scheduleIndex);
    setIsVideoModalOpen(true);
  };

  const handleVideoModalClose = () => {
    setIsVideoModalOpen(false);
    setCurrentScheduleIndex(null);
  };

  const handleSubmitVideo = (videoArray) => {
    if (currentScheduleIndex !== null) {
      const newSchedules = schedules.map((schedule, i) =>
        i === currentScheduleIndex
          ? {
              ...schedule,
              videos: [...(schedule.videos || []), ...videoArray],
            }
          : schedule
      );
      setSchedules(newSchedules);

      // Notify parent about schedule changes
      onChange({
        schedules: newSchedules,
      });
    }
    handleVideoModalClose();
  };

  const handleRemoveVideo = (scheduleIndex, videoIndex) => {
    const newSchedules = schedules.map((schedule, i) =>
      i === scheduleIndex
        ? {
            ...schedule,
            videos: schedule.videos.filter((_, j) => j !== videoIndex),
          }
        : schedule
    );
    setSchedules(newSchedules);

    // Notify parent about schedule changes
    onChange({
      schedules: newSchedules,
    });
  };

  const handleVideoFrequencyChange = (scheduleIndex, videoIndex, frequency) => {
    const newSchedules = schedules.map((schedule, i) =>
      i === scheduleIndex
        ? {
            ...schedule,
            videos: schedule.videos.map((video, j) =>
              j === videoIndex ? { ...video, frequency } : video
            ),
          }
        : schedule
    );
    setSchedules(newSchedules);

    // Notify parent about schedule changes
    onChange({
      schedules: newSchedules,
    });
  };

  const handleFormikSubmit = (values) => {
    if (!schedules.length) {
      handleStatus(
        "Adauga cel putin o perioada pentru campanie.",
        setToastStatus,
        setToastDescription
      );
      return;
    }

    // Check for duplicate periods (same start and end dates)
    const duplicatePeriods = [];
    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        if (
          schedules[i].startDate === schedules[j].startDate &&
          schedules[i].endDate === schedules[j].endDate
        ) {
          duplicatePeriods.push({
            period1: i + 1,
            period2: j + 1,
            startDate: schedules[i].startDate,
            endDate: schedules[i].endDate,
          });
        }
      }
    }

    if (duplicatePeriods.length > 0) {
      const duplicateMessages = duplicatePeriods.map(
        (dup) =>
          `Perioada ${dup.period1} si Perioada ${dup.period2} (${moment(
            dup.startDate
          ).format("DD.MM.YYYY")} - ${moment(dup.endDate).format(
            "DD.MM.YYYY"
          )})`
      );
      handleStatus(
        `Urmatoarele perioade au date identice si trebuie sa fie diferite: ${duplicateMessages.join(
          ", "
        )}`,
        setToastStatus,
        setToastDescription
      );
      return;
    }

    // Check for periods without videos
    const periodsWithoutVideos = schedules
      .map((schedule, index) => ({ schedule, index }))
      .filter(
        ({ schedule }) => !schedule.videos || schedule.videos.length === 0
      )
      .map(({ index }) => index + 1);

    if (periodsWithoutVideos.length > 0) {
      const periodText =
        periodsWithoutVideos.length === 1
          ? `Perioada ${periodsWithoutVideos[0]} nu are`
          : `Perioadele ${periodsWithoutVideos.join(", ")} nu au`;

      handleStatus(
        `${periodText} niciun spot adaugat. Adauga cel putin un spot pentru fiecare perioada.`,
        setToastStatus,
        setToastDescription
      );
      return;
    }

    const formattedSchedules = schedules.map((schedule) => ({
      id: schedule.originalId || null,
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      videos: schedule.videos.map((video) => ({
        id: video.id,
        frequency: video.frequency,
      })),
    }));

    onChange({
      name: values.name,
      client: values.client,
      observations: values.observations,
      schedules: formattedSchedules,
    });

    goNext();
  };

  // const getCurrentCampaignStatusFromData = () => {
  //   // Use current schedules state instead of original data
  //   const currentSchedules = schedules;
  //   if (!currentSchedules || currentSchedules.length === 0)
  //     return "Campania este in asteptare";

  //   const currentDate = moment().startOf("day");

  //   const isOngoing = currentSchedules.some((schedule) => {
  //     const start = schedule.startDate
  //       ? moment(schedule.startDate, "YYYY-MM-DD").startOf("day")
  //       : null;
  //     const end = schedule.endDate
  //       ? moment(schedule.endDate, "YYYY-MM-DD").startOf("day")
  //       : moment("9999-12-31");

  //     if (!start) return false;
  //     return (
  //       currentDate.isSameOrAfter(start) && currentDate.isSameOrBefore(end)
  //     );
  //   });

  //   const isCompleted = currentSchedules.every((schedule) => {
  //     if (!schedule.endDate) return false;
  //     const end = moment(schedule.endDate, "YYYY-MM-DD").startOf("day");
  //     return currentDate.isAfter(end);
  //   });

  //   if (isOngoing) return "Campania este in desfasurare";
  //   if (isCompleted) return "Campania este finalizata";

  //   return "Campania este in asteptare";
  // };

  // Initialize schedules from data
  useEffect(() => {
    // Only initialize if schedules state is empty to prevent losing data when switching tabs
    if (schedules.length === 0) {
      if (initialCalendars && initialCalendars.length > 0) {
        const mappedSchedules = initialCalendars.map((cal) => ({
          id: uuidv4(),
          originalId: cal.id,
          startDate: cal.startDate,
          endDate: cal.endDate,
          videos: cal.videos || [], // Use videos specific to this schedule, not combined videos
        }));

        // Store original values for validation
        const originalSchedulesData = initialCalendars.map((cal) => ({
          id: cal.id,
          startDate: cal.startDate,
          endDate: cal.endDate,
        }));

        setSchedules(mappedSchedules);
        setOriginalSchedules(originalSchedulesData);
      } else {
        // Default schedule
        const defaultSchedule = {
          id: uuidv4(),
          startDate: moment().format("YYYY-MM-DD"),
          endDate: moment().format("YYYY-MM-DD"),
          videos: videos || [],
        };
        setSchedules([defaultSchedule]);
        setOriginalSchedules([]); // No original data for new schedules
      }
    }
  }, [initialCalendars, videos, schedules.length]);

  return (
    <>
      <Formik
        initialValues={{
          name: data?.name || "",
          client: data?.client || "",
          observations: data?.observations || "",
        }}
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={handleFormikSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form style={styles.container}>
            <div style={styles.leftSection}>
              <TextField
                sx={styles.select}
                name="name"
                label="Nume Campanie"
                variant="outlined"
                fullWidth
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              <TextField
                sx={styles.select}
                name="client"
                label="Nume Client"
                variant="outlined"
                fullWidth
                value={values.client}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.client && Boolean(errors.client)}
                helperText={touched.client && errors.client}
              />

              <TextField
                sx={styles.select}
                name="observations"
                label="Observatii"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={values.observations}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <div
                style={{
                  padding: "20px",
                  background: "linear-gradient(135deg, #f8f9fa, #ffffff)",
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.8)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background:
                        values.name && values.client ? "#22c55e" : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "14px",
                      fontFamily: "SeagoeUIBold",
                    }}
                  >
                    {values.name && values.client ? "✓" : "1"}
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color:
                        values.name && values.client ? "#22c55e" : "#64748b",
                      fontFamily:
                        values.name && values.client
                          ? "SeagoeUIBold"
                          : "SeagoeUI",
                    }}
                  >
                    Informatii campanie
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background:
                        schedules.length > 0 &&
                        schedules.every((s) => s.videos?.length > 0)
                          ? "#22c55e"
                          : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "14px",
                      fontFamily: "SeagoeUIBold",
                    }}
                  >
                    {schedules.length > 0 &&
                    schedules.every((s) => s.videos?.length > 0)
                      ? "✓"
                      : "2"}
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color:
                        schedules.length > 0 &&
                        schedules.every((s) => s.videos?.length > 0)
                          ? "#22c55e"
                          : "#64748b",
                      fontFamily:
                        values.name && values.client
                          ? "SeagoeUIBold"
                          : "SeagoeUI",
                    }}
                  >
                    Perioade si spoturi
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.rightSection}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "24px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontFamily: "SeagoeUI",
                      color: "#ff4757",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                    }}
                  >
                    PERIOADE CAMPANIE
                  </span>
                  <h2
                    style={{
                      fontSize: "2.5rem",
                      fontFamily: "SeagoeUIBold",
                      color: "#2c3e50",
                      margin: 0,
                      position: "relative",
                    }}
                  >
                    Perioade & Spoturi
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-4px",
                        left: 0,
                        width: "40%",
                        height: "4px",
                        background: "#ff4757",
                        borderRadius: "2px",
                      }}
                    />
                  </h2>
                </div>
                <div
                  style={{
                    height: "2px",
                    flex: 1,
                    marginLeft: "24px",
                    background:
                      "linear-gradient(to right, rgba(255, 71, 87, 0.5) 0%, transparent 100%)",
                  }}
                />
              </div>

              <div
                style={{
                  padding: "16px",
                  background: "rgba(34, 197, 94, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(34, 197, 94, 0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#16a34a",
                    fontFamily: "SeagoeUI",
                    lineHeight: "1.5",
                  }}
                >
                  <label style={{ fontFamily: "SeagoeUIBold" }}>
                    Editare campanie:
                  </label>
                  <br />
                  1. Modifica perioadele existente sau adauga perioade noi
                  <br />
                  2. Adauga, sterge sau modifica frecventa spoturilor
                  <br />
                  3. Pentru campaniile active, datele originale raman valide
                  <br />
                  4. Continua la selectia playerelor pentru a salva modificarile
                </div>
              </div>

              <CustomButton
                text="Adauga Perioada"
                onClick={handleAddSchedule}
                icon={<Plus size={20} />}
                style={{
                  background: "linear-gradient(135deg, #ff4757, #ff6b7a)",
                }}
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {schedules.map((schedule, scheduleIndex) => (
                  <Card
                    key={schedule.id}
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(148,163,184,0.1)",
                      border: "1px solid rgba(226,232,240,0.8)",
                    }}
                  >
                    <Accordion
                      expanded={expandedAccordion === scheduleIndex}
                      onChange={handleAccordionChange(scheduleIndex)}
                    >
                      <AccordionSummary
                        expandIcon={<ChevronDown />}
                        style={{
                          background:
                            "linear-gradient(135deg, #f8f9fa, #ffffff)",
                          borderRadius: "16px 16px 0 0",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            width: "100%",
                          }}
                        >
                          <Calendar size={20} color="#ff4757" />
                          <Typography
                            variant="h6"
                            style={{
                              fontFamily: "SeagoeUIBold",
                              color: "#2c3e50",
                              flex: 1,
                            }}
                          >
                            Spoturi Perioada {scheduleIndex + 1}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748b",
                              marginRight: "8px",
                            }}
                          >
                            {moment(schedule.startDate).format("DD.MM.YYYY")} →{" "}
                            {moment(schedule.endDate).format("DD.MM.YYYY")}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748b",
                              marginRight: "8px",
                            }}
                          >
                            {schedule.videos?.length || 0} spoturi
                          </Typography>
                          <Tooltip title="Sterge perioada">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSchedule(scheduleIndex);
                              }}
                              size="medium"
                              style={{
                                color: "#ef4444",
                              }}
                            >
                              <MinusSquare size={22} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "16px",
                            }}
                          >
                            <TextField
                              type="date"
                              label="Data inceput"
                              value={schedule.startDate}
                              onChange={(e) =>
                                handleScheduleChange(
                                  scheduleIndex,
                                  "startDate",
                                  e.target.value
                                )
                              }
                              onBlur={(e) =>
                                handleScheduleBlur(
                                  scheduleIndex,
                                  "startDate",
                                  e.target.value
                                )
                              }
                              sx={{
                                ...styles.select,
                                flex: 1,
                              }}
                              inputProps={{
                                min: schedule.originalId
                                  ? undefined
                                  : moment().format("YYYY-MM-DD"),
                                max: "2030-12-31",
                              }}
                              helperText={
                                schedule.originalId
                                  ? "Pentru campaniile existente, data poate fi modificata"
                                  : "Data nu poate fi in trecut"
                              }
                            />
                            <TextField
                              type="date"
                              label="Data sfarsit"
                              value={schedule.endDate}
                              onChange={(e) =>
                                handleScheduleChange(
                                  scheduleIndex,
                                  "endDate",
                                  e.target.value
                                )
                              }
                              onBlur={(e) =>
                                handleScheduleBlur(
                                  scheduleIndex,
                                  "endDate",
                                  e.target.value
                                )
                              }
                              sx={{
                                ...styles.select,
                                flex: 1,
                              }}
                              inputProps={{
                                min:
                                  schedule.startDate ||
                                  (schedule.originalId
                                    ? undefined
                                    : moment().format("YYYY-MM-DD")),
                                max: "2030-12-31",
                              }}
                              helperText="Data nu poate fi inainte de data de inceput"
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginTop: "16px",
                            }}
                          >
                            <Video size={20} color="#ff4757" />
                            <Typography
                              variant="h6"
                              style={{
                                fontFamily: "SeagoeUIBold",
                                color: "#2c3e50",
                                flex: 1,
                              }}
                            >
                              Spoturi pentru aceasta perioada de difuzare
                            </Typography>
                            <CustomButton
                              text="Adauga Spot"
                              onClick={() =>
                                handleOpenVideoModal(scheduleIndex)
                              }
                              style={{
                                padding: "8px 16px",
                                fontSize: "14px",
                              }}
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {schedule.videos?.map((video, videoIndex) => (
                              <div
                                key={videoIndex}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                  padding: "12px 16px",
                                  background: "#f8f9fa",
                                  borderRadius: "12px",
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                <div
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "6px",
                                    background: "rgba(255,71,87,0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#ff4757",
                                    fontSize: "12px",
                                  }}
                                >
                                  {videoIndex + 1}
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    fontSize: "14px",
                                    color: "#334155",
                                  }}
                                >
                                  {video.title}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#64748b",
                                    }}
                                  >
                                    Frecventa pe ora:
                                  </span>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={video.frequency || 1}
                                    onChange={(e) =>
                                      handleVideoFrequencyChange(
                                        scheduleIndex,
                                        videoIndex,
                                        parseInt(e.target.value) || 1
                                      )
                                    }
                                    inputProps={{
                                      min: 1,
                                      style: {
                                        width: "60px",
                                        textAlign: "center",
                                        padding: "4px",
                                      },
                                    }}
                                  />
                                </div>
                                <Tooltip title="Sterge spotul">
                                  <IconButton
                                    onClick={() =>
                                      handleRemoveVideo(
                                        scheduleIndex,
                                        videoIndex
                                      )
                                    }
                                    size="medium"
                                    style={{
                                      color: "#ef4444",
                                    }}
                                  >
                                    <MinusSquare size={20} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            ))}
                            {(!schedule.videos ||
                              schedule.videos.length === 0) && (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#64748b",
                                  fontSize: "14px",
                                  fontStyle: "italic",
                                  background: "#f8f9fa",
                                  borderRadius: "12px",
                                  border: "1px dashed #cbd5e1",
                                }}
                              >
                                Nu sunt spoturi adaugate pentru aceasta perioada
                              </div>
                            )}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </Card>
                ))}
              </div>

              {schedules.length === 0 && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: "16px",
                    background: "rgba(241,245,249,0.5)",
                    borderRadius: "16px",
                    border: "1px dashed rgba(148,163,184,0.4)",
                  }}
                >
                  Nu exista perioade adaugate inca
                </div>
              )}

              <div
                style={{
                  marginTop: "5px",
                  padding: "24px",
                  background: "linear-gradient(135deg, #f8f9fa, #ffffff)",
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.8)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#64748b",
                    fontFamily: "SeagoeUI",
                  }}
                >
                  {schedules.length === 0
                    ? "Adauga cel putin o perioada cu spoturi pentru a continua"
                    : schedules.some((s) => !s.videos || s.videos.length === 0)
                    ? "Adauga spoturi pentru toate perioadele pentru a continua"
                    : "Toate informatiile sunt complete! Poti trece la urmatorul pas."}
                </div>
                <button
                  type="submit"
                  className="add-video-button"
                  style={{
                    opacity:
                      schedules.length > 0 &&
                      schedules.every((s) => s.videos?.length > 0)
                        ? 1
                        : 0.5,
                    cursor:
                      schedules.length > 0 &&
                      schedules.every((s) => s.videos?.length > 0)
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  Continua la Selectie Playere →
                </button>
              </div>

              <AddVideoDialog
                open={isVideoModalOpen}
                onClose={handleVideoModalClose}
                onSubmit={handleSubmitVideo}
                onSelect={handleSubmitVideo}
                instantUpload={false}
                isCampaign={true}
                canAddVideo={false}
              />
            </div>
          </Form>
        )}
      </Formik>
      <AlertModal
        open={alertDeleteSchedule}
        handleClose={() => {
          setAlertDeleteSchedule(false);
          setScheduleToRemove(null);
        }}
        text="Esti sigur ca vrei sa stergi aceasta perioada?"
        handleYes={executeDeleteSchedule}
      />
    </>
  );
}
